/**
 * 动态隐私政策页面组件
 * 根据用户区域动态获取并渲染隐私政策内容
 *
 * 功能：
 * - 从 RegionStore 读取当前区域
 * - 调用 Public Agreements API 获取隐私政策
 * - 渲染 HTML 内容
 * - 处理加载和错误状态
 * - 支持锚点定位（如 #cookies, #do-not-sell）
 * - 国内模式下强制使用 CN 区域协议
 *
 * **Validates: Requirements 2.2, 2.3, 2.5, 2.6**
 */

import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import SubsectionHeader from "@/components/shared/SubsectionHeader";
import Footer from "@/components/Footer";
import { Loader2, AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useRegionStore } from "@/stores/regionStore";
import { useBootstrap } from "@/hooks/useBootstrap";
import { useOperationRegion } from "@/hooks/useOperationRegion";
import apiClient from "@/lib/apiClient";

/**
 * 协议数据接口
 */
interface AgreementData {
  id: number;
  type: string;
  version: string;
  content: string;
  region_code: string;
  published_at: string;
}

/**
 * 动态隐私政策页面
 */
const PrivacyPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const contentRef = useRef<HTMLDivElement>(null);
  const [agreement, setAgreement] = useState<AgreementData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 从 RegionStore 获取区域信息
  const bootstrapData = useRegionStore((state) => state.bootstrapData);

  // 如果 Store 中没有数据，调用 Bootstrap API 获取
  const { data: freshBootstrap, isLoading: bootstrapLoading } = useBootstrap();

  // 获取运营区域
  const { region: operationRegion, loading: operationRegionLoading } =
    useOperationRegion();

  // 确定当前区域
  // 国内模式下强制使用 CN，否则使用检测到的区域
  const currentRegion =
    operationRegion === "domestic"
      ? "CN"
      : bootstrapData?.detected_region ||
        freshBootstrap?.detected_region ||
        "GLOBAL";

  /**
   * 获取协议内容
   */
  const fetchAgreement = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiClient<AgreementData>(
        `/api/v1/public/agreements/latest?type=privacy&region=${encodeURIComponent(currentRegion)}`,
        { disableAutoToast: true }
      );
      setAgreement(data);
    } catch (err) {
      console.error("Failed to fetch privacy policy:", err);
      setError(
        err instanceof Error
          ? err.message
          : t("legal.loadError", "无法加载隐私政策")
      );
    } finally {
      setLoading(false);
    }
  };

  // 当区域确定后获取协议内容
  useEffect(() => {
    if (!bootstrapLoading && !operationRegionLoading) {
      fetchAgreement();
    }
  }, [currentRegion, bootstrapLoading, operationRegionLoading]);

  // 内容加载完成后处理锚点定位
  useEffect(() => {
    if (!loading && agreement && location.hash) {
      // 延迟执行以确保 DOM 已渲染
      const timer = setTimeout(() => {
        const hash = location.hash.slice(1); // 移除 # 符号

        // 首先尝试查找精确匹配的 id
        let targetElement = document.getElementById(hash);

        // 如果没有找到精确匹配，尝试在内容中查找包含关键词的标题
        if (!targetElement && contentRef.current) {
          const headings = contentRef.current.querySelectorAll(
            "h1, h2, h3, h4, h5, h6"
          );
          const keywords: Record<string, string[]> = {
            cookies: ["cookie", "Cookie", "COOKIE", "cookies", "Cookies"],
            "do-not-sell": ["do not sell", "Do Not Sell", "不出售", "禁止出售"],
            ccpa: ["CCPA", "California", "加州"],
            gdpr: ["GDPR", "欧盟", "European"],
          };

          const searchTerms = keywords[hash] || [hash];

          for (const heading of headings) {
            const text = heading.textContent || "";
            if (
              searchTerms.some((term) =>
                text.toLowerCase().includes(term.toLowerCase())
              )
            ) {
              targetElement = heading as HTMLElement;
              break;
            }
          }
        }

        if (targetElement) {
          targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [loading, agreement, location.hash]);

  /**
   * 格式化发布日期
   */
  const formatPublishedDate = (
    dateString: string | null | undefined
  ): string => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      // 根据当前 i18n 语言设置 locale
      const locale = i18n.language || "zh-CN";
      return date.toLocaleDateString(locale, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "";
    }
  };

  /**
   * 构建描述文本
   */
  const buildDescription = (): string => {
    if (!agreement) {
      return t("legal.privacyDescription", "隐私政策");
    }

    const parts: string[] = [];

    if (agreement.version) {
      parts.push(t("legal.version", "版本") + " " + agreement.version);
    }

    if (agreement.published_at) {
      parts.push(
        t("legal.updatedAt", "更新时间") +
          "：" +
          formatPublishedDate(agreement.published_at)
      );
    }

    if (agreement.region_code) {
      parts.push(t("legal.region", "区域") + "：" + agreement.region_code);
    }

    return parts.join(" | ");
  };

  // 加载状态
  if (loading || bootstrapLoading || operationRegionLoading) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <div className="flex-1 px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <SubsectionHeader
            title={t("legal.privacyTitle", "隐私政策")}
            description={t("legal.loading", "加载中...")}
            icon="Shield"
            variant="hero"
          />
          <div className="py-8 sm:py-16">
            <div className="mx-auto max-w-4xl">
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // 错误状态
  if (error) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <div className="flex-1 px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <SubsectionHeader
            title={t("legal.privacyTitle", "隐私政策")}
            description={t("legal.errorOccurred", "加载出错")}
            icon="Shield"
            variant="hero"
          />
          <div className="py-8 sm:py-16">
            <div className="mx-auto max-w-4xl">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{t("legal.errorTitle", "加载失败")}</AlertTitle>
                <AlertDescription className="mt-2">
                  <p>{error}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={fetchAgreement}
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    {t("common.retry", "重试")}
                  </Button>
                </AlertDescription>
              </Alert>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // 协议不存在
  if (!agreement) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <div className="flex-1 px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <SubsectionHeader
            title={t("legal.privacyTitle", "隐私政策")}
            description={t("legal.notFound", "协议不存在")}
            icon="Shield"
            variant="hero"
          />
          <div className="py-8 sm:py-16">
            <div className="mx-auto max-w-4xl">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>
                  {t("legal.notFoundTitle", "未找到协议")}
                </AlertTitle>
                <AlertDescription>
                  {t(
                    "legal.notFoundDescription",
                    "当前区域的隐私政策暂不可用，请稍后再试。"
                  )}
                </AlertDescription>
              </Alert>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // 判断内容是否为 HTML
  const isHtmlContent = (content: string): boolean => {
    return /<[a-z][\s\S]*>/i.test(content);
  };

  // 正常渲染协议内容
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="flex-1 px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <SubsectionHeader
          title={t("legal.privacyTitle", "隐私政策")}
          description={buildDescription()}
          icon="Shield"
          variant="hero"
        />

        <div className="py-8 sm:py-16">
          <div className="mx-auto max-w-4xl">
            <article
              ref={contentRef}
              className="prose prose-sm sm:prose-base dark:prose-invert max-w-none leading-relaxed"
            >
              {isHtmlContent(agreement.content) ? (
                <div dangerouslySetInnerHTML={{ __html: agreement.content }} />
              ) : (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {agreement.content}
                </ReactMarkdown>
              )}
            </article>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPage;
